import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Col, Row, Card, CardBody, Badge, Button, Modal, ModalHeader, ModalBody,
  ModalFooter, Spinner, Input, Label, FormGroup, Alert, Table
} from "reactstrap";
import Dropzone from "react-dropzone";
import { Link } from "react-router-dom";
import Breadcrumbs from "../../../components/Common/Breadcrumb";

const BASE_URL = "https://harmanagelapi.stechomeyazilim.info";

const KategoriYonetimi = () => {
  document.title = "Kategori Yönetimi | HarmanaGEL";

  const authUser = JSON.parse(localStorage.getItem("authUser"));
  const token = localStorage.getItem("token") || authUser?.Token;
  const headers = useMemo(() => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  }), [token]);

  // ─── DATA STATES ──────────────────────────────────────────────
  const [kategoriler, setKategoriler] = useState([]);
  const [baglantiler, setBaglantiler] = useState([]);
  const [ilanSayilari, setIlanSayilari] = useState({});   // { kategoriId: count }
  const [teklifSayilari, setTeklifSayilari] = useState({}); // { kategoriId: count }
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");

  // ─── UI STATES ────────────────────────────────────────────────
  const [activeFilter, setActiveFilter] = useState("all"); // 'all' | 'ana' | 'alt'
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState("");

  // ─── ADD/EDIT MODAL ───────────────────────────────────────────
  const [modal, setModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    AD: "", SIRA_NO: "", MEZAT_ACIK_MI: false, SADECE_ILAN_MI: true,
    IsPremium: false, Degerle_IsCheck: false,
  });
  const [secilenUstKategoriId, setSecilenUstKategoriId] = useState("");

  // ─── VIEW LISTINGS MODAL ──────────────────────────────────────
  const [ilanModal, setIlanModal] = useState(false);
  const [seciliKategori, setSeciliKategori] = useState(null);
  const [kategoriIlanlari, setKategoriIlanlari] = useState([]);
  const [isLoadingIlanlar, setIsLoadingIlanlar] = useState(false);

  // ─── BAGLANTI MODAL ───────────────────────────────────────────
  const [baglantiModal, setBaglantiModal] = useState(false);
  const [baglantiKategori, setBaglantiKategori] = useState(null);
  const [yeniBaglantiId, setYeniBaglantiId] = useState("");
  const [isSavingBaglanti, setIsSavingBaglanti] = useState(false);

  // ─── FETCH ALL DATA ───────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [kRes, bRes, ilanRes, teklifRes] = await Promise.all([
        fetch(`${BASE_URL}/KATEGORILER?$orderby=SIRA_NO asc,ID asc`, { headers }),
        fetch(`${BASE_URL}/KATEGORI_BAGLANTILARI`, { headers }),
        fetch(`${BASE_URL}/ILANLAR?$select=ID,KATEGORI_ID&$top=5000`, { headers }),
        fetch(`${BASE_URL}/ILAN_TEKLIFLERI?$select=ID,ILAN_ID&$top=5000`, { headers }),
      ]);

      const [kData, bData, ilanData, teklifData] = await Promise.all([
        kRes.json(), bRes.json(), ilanRes.json(), teklifRes.json(),
      ]);

      const kats = kData.value || [];
      const bags = bData.value || [];
      const ilanlar = ilanData.value || [];
      const teklifler = teklifData.value || [];

      // İlan sayıları: { kategoriId → count }
      const ilanCounts = {};
      ilanlar.forEach(i => {
        if (i.KATEGORI_ID) ilanCounts[i.KATEGORI_ID] = (ilanCounts[i.KATEGORI_ID] || 0) + 1;
      });

      // Teklif sayıları: ilan → kategori map üzerinden
      const ilanKategoriMap = {};
      ilanlar.forEach(i => { ilanKategoriMap[i.ID] = i.KATEGORI_ID; });
      const teklifCounts = {};
      teklifler.forEach(t => {
        const katId = ilanKategoriMap[t.ILAN_ID];
        if (katId) teklifCounts[katId] = (teklifCounts[katId] || 0) + 1;
      });

      setKategoriler(kats);
      setBaglantiler(bags);
      setIlanSayilari(ilanCounts);
      setTeklifSayilari(teklifCounts);
    } catch {
      setError("Veriler yüklenirken hata oluştu.");
    } finally {
      setIsLoading(false);
    }
  }, [headers]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ─── COMPUTED VALUES ──────────────────────────────────────────
  const altKategoriIdSet = useMemo(
    () => new Set(baglantiler.map(b => b.ALT_KATEGORI_ID)),
    [baglantiler]
  );

  const kategoriMap = useMemo(() => {
    const m = {};
    kategoriler.forEach(k => { m[k.ID] = k; });
    return m;
  }, [kategoriler]);

  // Ana kategori ID → alt kategori ID listesi
  const altByParent = useMemo(() => {
    const m = {};
    baglantiler.forEach(b => {
      if (!m[b.UST_KATEGORI_ID]) m[b.UST_KATEGORI_ID] = [];
      m[b.UST_KATEGORI_ID].push({ baglantiId: b.ID, altId: b.ALT_KATEGORI_ID });
    });
    return m;
  }, [baglantiler]);

  // Alt kategori ID → üst kategori ID listesi
  const parentsByAlt = useMemo(() => {
    const m = {};
    baglantiler.forEach(b => {
      if (!m[b.ALT_KATEGORI_ID]) m[b.ALT_KATEGORI_ID] = [];
      m[b.ALT_KATEGORI_ID].push(b.UST_KATEGORI_ID);
    });
    return m;
  }, [baglantiler]);

  const stats = useMemo(() => {
    const anaCount = kategoriler.filter(k => !altKategoriIdSet.has(k.ID)).length;
    const altCount = altKategoriIdSet.size;
    const totalIlan = Object.values(ilanSayilari).reduce((a, b) => a + b, 0);
    const totalTeklif = Object.values(teklifSayilari).reduce((a, b) => a + b, 0);
    return { total: kategoriler.length, ana: anaCount, alt: altCount, totalIlan, totalTeklif };
  }, [kategoriler, altKategoriIdSet, ilanSayilari, teklifSayilari]);

  const anaKategoriler = useMemo(
    () => kategoriler.filter(k => !altKategoriIdSet.has(k.ID)),
    [kategoriler, altKategoriIdSet]
  );

  const filteredKategoriler = useMemo(() => {
    let list =
      activeFilter === "ana" ? kategoriler.filter(k => !altKategoriIdSet.has(k.ID))
      : activeFilter === "alt" ? kategoriler.filter(k => altKategoriIdSet.has(k.ID))
      : kategoriler;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(k => k.AD?.toLowerCase().includes(q));
    }
    return list;
  }, [kategoriler, altKategoriIdSet, activeFilter, searchQuery]);

  // ─── FILE UPLOAD ──────────────────────────────────────────────
  const handleAcceptedFiles = (files) => {
    const file = files[0];
    Object.assign(file, { preview: URL.createObjectURL(file) });
    setSelectedFile(file);
    setImagePreview(file.preview);
  };

  // ─── MODAL HELPERS ────────────────────────────────────────────
  const resetModal = () => {
    setFormData({ AD: "", SIRA_NO: "", MEZAT_ACIK_MI: false, SADECE_ILAN_MI: true, IsPremium: false, Degerle_IsCheck: false });
    setIsEditing(false);
    setSelectedFile(null);
    setImagePreview("");
    setSecilenUstKategoriId("");
  };

  const toggleModal = () => {
    if (modal) resetModal();
    setModal(v => !v);
  };

  const handleEdit = (k) => {
    setFormData({
      ID: k.ID, AD: k.AD || "", SIRA_NO: k.SIRA_NO ?? "",
      MEZAT_ACIK_MI: !!k.MEZAT_ACIK_MI, SADECE_ILAN_MI: !!k.SADECE_ILAN_MI,
      IsPremium: !!k.IsPremium, Degerle_IsCheck: !!k.Degerle_IsCheck,
      KATEGORILER_IMAGE: k.KATEGORILER_IMAGE || "",
    });
    setImagePreview(k.KATEGORILER_IMAGE || "");
    const parents = parentsByAlt[k.ID];
    setSecilenUstKategoriId(parents?.[0]?.toString() || "");
    setIsEditing(true);
    setModal(true);
  };

  const handleSave = async () => {
    if (!formData.AD.trim()) { setError("Kategori adı zorunludur."); return; }
    setIsSaving(true);
    try {
      let imagePath = formData.KATEGORILER_IMAGE || null;

      if (selectedFile) {
        const fileData = new FormData();
        fileData.append("file", selectedFile);
        const uploadRes = await fetch(
          "https://harmanageladmin.stechomeyazilim.info/Uploads/upload.php",
          { method: "POST", body: fileData }
        );
        const uploadResult = await uploadRes.json();
        if (uploadResult.status) {
          imagePath = "https://harmanageladmin.stechomeyazilim.info/Uploads/" + uploadResult.file_name;
        }
      }

      const payload = {
        AD: formData.AD,
        SIRA_NO: formData.SIRA_NO !== "" && formData.SIRA_NO !== null && formData.SIRA_NO !== undefined
          ? parseInt(formData.SIRA_NO)
          : null,
        MEZAT_ACIK_MI: formData.MEZAT_ACIK_MI,
        SADECE_ILAN_MI: formData.SADECE_ILAN_MI,
        IsPremium: formData.IsPremium,
        Degerle_IsCheck: formData.Degerle_IsCheck,
        KATEGORILER_IMAGE: imagePath,
      };

      const url = isEditing
        ? `${BASE_URL}/KATEGORILER(${formData.ID})`
        : `${BASE_URL}/KATEGORILER`;

      const res = await fetch(url, {
        method: isEditing ? "PATCH" : "POST",
        headers, body: JSON.stringify(payload),
      });

      if (res.ok) {
        let savedId = formData.ID;
        if (!isEditing) {
          try { const saved = await res.json(); savedId = saved.ID || saved.id || saved.value?.[0]?.ID; }
          catch { /* PATCH returns 204 */ }
        }

        // SADECE_ILAN_MI cascade: tüm alt ve alt-altı kategorilere özyinelemeli yay
        if (isEditing && formData.ID) {
          const getAllDescendants = (id) => {
            const children = altByParent[id] || [];
            const result = [];
            children.forEach(({ altId }) => {
              result.push(altId);
              result.push(...getAllDescendants(altId));
            });
            return result;
          };
          const allDescendants = getAllDescendants(formData.ID);
          if (allDescendants.length > 0) {
            await Promise.all(allDescendants.map(altId =>
              fetch(`${BASE_URL}/KATEGORILER(${altId})`, {
                method: "PATCH",
                headers,
                body: JSON.stringify({ SADECE_ILAN_MI: payload.SADECE_ILAN_MI }),
              })
            ));
          }
        }

        // Üst kategori bağlantısı ekle/güncelle
        if (secilenUstKategoriId && savedId) {
          const ustId = parseInt(secilenUstKategoriId);
          const existingBag = baglantiler.find(
            b => b.ALT_KATEGORI_ID === savedId && b.UST_KATEGORI_ID === ustId
          );
          if (!existingBag) {
            await fetch(`${BASE_URL}/KATEGORI_BAGLANTILARI`, {
              method: "POST", headers,
              body: JSON.stringify({ UST_KATEGORI_ID: ustId, ALT_KATEGORI_ID: savedId }),
            });
          }
        } else if (!secilenUstKategoriId && isEditing && formData.ID) {
          // Üst kategori kaldırıldıysa eski bağlantıları sil
          const eskiBaglar = baglantiler.filter(b => b.ALT_KATEGORI_ID === formData.ID);
          await Promise.all(eskiBaglar.map(b =>
            fetch(`${BASE_URL}/KATEGORI_BAGLANTILARI(${b.ID})`, { method: "DELETE", headers })
          ));
        }

        setSuccessMsg(isEditing ? "Kategori güncellendi." : "Kategori eklendi.");
        toggleModal();
        setTimeout(() => { fetchAll(); setSuccessMsg(""); }, 400);
      } else {
        setError("İşlem başarısız: " + res.status);
      }
    } catch {
      setError("Bir hata oluştu.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu kategoriyi silmek istediğinize emin misiniz?")) return;
    try {
      await fetch(`${BASE_URL}/KATEGORILER(${id})`, { method: "DELETE", headers });
      setSuccessMsg("Kategori silindi.");
      fetchAll();
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch {
      setError("Silme sırasında hata oluştu.");
    }
  };

  // ─── VIEW LISTINGS ────────────────────────────────────────────
  const viewIlanlar = async (k) => {
    setSeciliKategori(k);
    setIlanModal(true);
    setIsLoadingIlanlar(true);
    setKategoriIlanlari([]);
    try {
      const res = await fetch(
        `${BASE_URL}/ILANLAR?$filter=KATEGORI_ID eq ${k.ID}&$expand=KULLANICILAR&$orderby=ID desc&$top=50`,
        { headers }
      );
      const data = await res.json();
      if (data.value) setKategoriIlanlari(data.value);
    } catch { }
    finally { setIsLoadingIlanlar(false); }
  };

  // ─── BAGLANTI MANAGEMENT ──────────────────────────────────────
  const openBaglantiModal = (k) => {
    setBaglantiKategori(k);
    setYeniBaglantiId("");
    setBaglantiModal(true);
  };

  const handleAddBaglanti = async () => {
    if (!yeniBaglantiId || !baglantiKategori) return;
    setIsSavingBaglanti(true);
    try {
      await fetch(`${BASE_URL}/KATEGORI_BAGLANTILARI`, {
        method: "POST", headers,
        body: JSON.stringify({
          UST_KATEGORI_ID: baglantiKategori.ID,
          ALT_KATEGORI_ID: parseInt(yeniBaglantiId),
        }),
      });
      setSuccessMsg("Alt kategori bağlandı.");
      setYeniBaglantiId("");
      fetchAll();
      setTimeout(() => setSuccessMsg(""), 2000);
    } catch {
      setError("Bağlantı eklenemedi.");
    } finally {
      setIsSavingBaglanti(false);
    }
  };

  const handleDeleteBaglanti = async (baglantiId) => {
    if (!window.confirm("Bu bağlantıyı kaldırmak istiyor musunuz?")) return;
    try {
      await fetch(`${BASE_URL}/KATEGORI_BAGLANTILARI(${baglantiId})`, {
        method: "DELETE", headers,
      });
      fetchAll();
    } catch {
      setError("Bağlantı kaldırılamadı.");
    }
  };

  const toggleExpand = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // ─── BAGlantı modal için listeler ────────────────────────────
  const mevcutAltKategoriler = baglantiKategori
    ? baglantiler
        .filter(b => b.UST_KATEGORI_ID === baglantiKategori.ID)
        .map(b => ({ ...b, altKategori: kategoriMap[b.ALT_KATEGORI_ID] }))
        .filter(b => b.altKategori)
    : [];

  const eklenebilirKategoriler = baglantiKategori
    ? kategoriler.filter(
        k =>
          k.ID !== baglantiKategori.ID &&
          !mevcutAltKategoriler.some(m => m.ALT_KATEGORI_ID === k.ID)
      )
    : [];

  // ─── ROW RENDERER ─────────────────────────────────────────────
  const renderRow = (k, isSubRow = false) => {
    const isAna = !altKategoriIdSet.has(k.ID);
    const parents = parentsByAlt[k.ID] || [];
    const altChildren = altByParent[k.ID] || [];
    const ilanCount = ilanSayilari[k.ID] || 0;
    const teklifCount = teklifSayilari[k.ID] || 0;
    const isExpanded = expandedIds.has(k.ID);

    return (
      <React.Fragment key={k.ID}>
        <tr className={isSubRow ? "table-light" : ""}>
          <td style={{ paddingLeft: isSubRow ? "2.5rem" : "1rem" }}>
            {isSubRow && <i className="bx bx-subdirectory-right text-muted me-1" />}
            {k.KATEGORILER_IMAGE ? (
              <img
                src={k.KATEGORILER_IMAGE}
                alt={k.AD}
                className="rounded"
                style={{ width: 36, height: 36, objectFit: "cover" }}
              />
            ) : (
              <div
                className="rounded bg-light d-inline-flex align-items-center justify-content-center text-muted border"
                style={{ width: 36, height: 36 }}
              >
                <i className="bx bx-image" />
              </div>
            )}
          </td>

          <td>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span className="fw-semibold">{k.AD}</span>
              {isAna ? (
                <Badge color="primary" pill className="font-size-10">Ana Kategori</Badge>
              ) : (
                <Badge color="secondary" pill className="font-size-10">Alt Kategori</Badge>
              )}
              {k.IsPremium && (
                <Badge color="warning" pill className="font-size-10">
                  <i className="bx bxs-star" /> Premium
                </Badge>
              )}
            </div>
            {/* Alt kategori → bağlı olduğu ana kategoriler */}
            {!isAna && parents.length > 0 && (
              <div className="mt-1 d-flex flex-wrap gap-1">
                <span className="text-muted font-size-11">Bağlı:</span>
                {parents.map(pId =>
                  kategoriMap[pId] ? (
                    <span key={pId} className="badge bg-light text-dark border font-size-10">
                      {kategoriMap[pId].AD}
                    </span>
                  ) : null
                )}
              </div>
            )}
            {/* Ana kategori → expand button */}
            {isAna && altChildren.length > 0 && (
              <button
                className="btn btn-link btn-sm p-0 mt-1 font-size-11 text-primary"
                onClick={() => toggleExpand(k.ID)}
              >
                <i className={`bx ${isExpanded ? "bx-chevron-up" : "bx-chevron-down"} me-1`} />
                {altChildren.length} alt kategori {isExpanded ? "gizle" : "göster"}
              </button>
            )}
          </td>

          <td className="text-center">
            <span className="fw-medium">{k.SIRA_NO ?? "-"}</span>
          </td>

          <td className="text-center">
            <span
              className={`badge font-size-12 ${ilanCount > 0 ? "bg-primary" : "bg-light text-dark"}`}
              style={{ minWidth: 32 }}
            >
              {ilanCount}
            </span>
          </td>

          <td className="text-center">
            <span
              className={`badge font-size-12 ${teklifCount > 0 ? "bg-success" : "bg-light text-dark"}`}
              style={{ minWidth: 32 }}
            >
              {teklifCount}
            </span>
          </td>

          <td>
            <div className="d-flex flex-wrap gap-1">
              {k.MEZAT_ACIK_MI && <Badge color="success" className="font-size-10">Mezat</Badge>}
              {k.SADECE_ILAN_MI && <Badge color="info" className="font-size-10">Sadece İlan</Badge>}
              {k.Degerle_IsCheck && <Badge color="purple" className="font-size-10" style={{ background: "#7367f0" }}>Değerleme</Badge>}
              {!k.MEZAT_ACIK_MI && !k.SADECE_ILAN_MI && !k.IsPremium && !k.Degerle_IsCheck && (
                <Badge color="light" className="text-dark font-size-10">Standart</Badge>
              )}
            </div>
          </td>

          <td>
            <div className="d-flex gap-1">
              <Button
                color="info" size="sm" outline
                title="İlanları Gör"
                onClick={() => viewIlanlar(k)}
              >
                <i className="bx bx-list-ul" />
              </Button>
              {isAna && (
                <Button
                  color="dark" size="sm" outline
                  title="Alt Kategorileri Yönet"
                  onClick={() => openBaglantiModal(k)}
                >
                  <i className="bx bx-git-branch" />
                </Button>
              )}
              <Button
                color="primary" size="sm" outline
                title="Düzenle"
                onClick={() => handleEdit(k)}
              >
                <i className="bx bx-pencil" />
              </Button>
              <Button
                color="danger" size="sm" outline
                title="Sil"
                onClick={() => handleDelete(k.ID)}
              >
                <i className="bx bx-trash" />
              </Button>
            </div>
          </td>
        </tr>

        {/* Expanded sub-rows */}
        {isAna && isExpanded && altChildren.map(({ altId }) => {
          const altKat = kategoriMap[altId];
          return altKat ? renderRow(altKat, true) : null;
        })}
      </React.Fragment>
    );
  };

  // ─── RENDER ───────────────────────────────────────────────────
  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Yönetim" breadcrumbItem="Kategori Yönetimi" />

          {successMsg && (
            <Alert color="success" className="border-0" toggle={() => setSuccessMsg("")}>
              <i className="mdi mdi-check-circle me-2" />{successMsg}
            </Alert>
          )}
          {error && (
            <Alert color="danger" className="border-0" toggle={() => setError(null)}>
              <i className="mdi mdi-alert-outline me-2" />{error}
            </Alert>
          )}

          {/* ── İSTATİSTİK KARTI ─────────────────────────────── */}
          <Row className="mb-4">
            {[
              { label: "Toplam Kategori", value: stats.total, icon: "bx-category", color: "primary" },
              { label: "Ana Kategori", value: stats.ana, icon: "bx-folder", color: "info" },
              { label: "Alt Kategori", value: stats.alt, icon: "bx-git-branch", color: "warning" },
              { label: "Toplam İlan", value: stats.totalIlan, icon: "bx-store-alt", color: "success" },
              { label: "Toplam Teklif", value: stats.totalTeklif, icon: "bx-message-square-dots", color: "danger" },
            ].map((s, i) => (
              <Col key={i} xs={6} md={4} xl>
                <Card className="mini-stats-wid mb-3">
                  <CardBody>
                    <div className="d-flex">
                      <div className="flex-grow-1">
                        <p className="text-muted fw-medium mb-1 font-size-13">{s.label}</p>
                        <h4 className="mb-0">
                          {isLoading ? <Spinner size="sm" color={s.color} /> : s.value}
                        </h4>
                      </div>
                      <div className={`avatar-sm rounded-circle bg-${s.color} align-self-center mini-stat-icon`}>
                        <span className={`avatar-title rounded-circle bg-${s.color}`}>
                          <i className={`bx ${s.icon} font-size-20`} />
                        </span>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>

          {/* ── ANA TABLO KARTI ───────────────────────────────── */}
          <Card className="shadow-sm">
            <CardBody>
              {/* Toolbar */}
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4">
                <div>
                  <h4 className="card-title mb-1">Kategori Hiyerarşisi</h4>
                  <p className="text-muted mb-0 font-size-13">
                    Ana ve alt kategorileri, istatistiklerini ve bağlantılarını yönetin.
                  </p>
                </div>
                <div className="d-flex gap-2 flex-wrap align-items-center">
                  <Input
                    type="search"
                    placeholder="Kategori ara..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    style={{ width: 180 }}
                    bsSize="sm"
                  />
                  <div className="btn-group btn-group-sm">
                    {[
                      { key: "all", label: "Tümü" },
                      { key: "ana", label: `Ana (${stats.ana})` },
                      { key: "alt", label: `Alt (${stats.alt})` },
                    ].map(f => (
                      <button
                        key={f.key}
                        className={`btn ${activeFilter === f.key ? "btn-primary" : "btn-outline-primary"}`}
                        onClick={() => setActiveFilter(f.key)}
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>
                  <Button
                    color="success"
                    size="sm"
                    className="btn-rounded"
                    onClick={() => { resetModal(); setModal(true); }}
                  >
                    <i className="bx bx-plus me-1" /> Yeni Kategori
                  </Button>
                </div>
              </div>

              {isLoading ? (
                <div className="text-center py-5">
                  <Spinner color="primary" />
                  <p className="mt-3 text-muted">Yükleniyor...</p>
                </div>
              ) : filteredKategoriler.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <i className="bx bx-category display-4 mb-3 opacity-50" />
                  <h5>Kategori bulunamadı.</h5>
                </div>
              ) : (
                <div className="table-responsive">
                  <Table className="table align-middle table-hover mb-0" bordered>
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: 56 }}>Görsel</th>
                        <th>Kategori Adı</th>
                        <th className="text-center" style={{ width: 72 }}>Sıra</th>
                        <th className="text-center" style={{ width: 80 }}>
                          <i className="bx bx-store-alt me-1 text-primary" />İlan
                        </th>
                        <th className="text-center" style={{ width: 80 }}>
                          <i className="bx bx-message-square-dots me-1 text-success" />Teklif
                        </th>
                        <th style={{ width: 200 }}>Özellikler</th>
                        <th style={{ width: 160 }}>İşlemler</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeFilter === "all" ? (
                        // Hiyerarşik görünüm: Önce ana kategoriler, altlarını expand ile
                        <>
                          {kategoriler
                            .filter(k => !altKategoriIdSet.has(k.ID))
                            .filter(k =>
                              !searchQuery.trim() ||
                              k.AD?.toLowerCase().includes(searchQuery.toLowerCase())
                            )
                            .map(k => renderRow(k, false))
                          }
                          {/* Bağlantısız alt kategoriler */}
                          {kategoriler
                            .filter(k => altKategoriIdSet.has(k.ID) &&
                              (parentsByAlt[k.ID] || []).length === 0
                            )
                            .filter(k =>
                              !searchQuery.trim() ||
                              k.AD?.toLowerCase().includes(searchQuery.toLowerCase())
                            )
                            .map(k => renderRow(k, false))
                          }
                        </>
                      ) : (
                        filteredKategoriler.map(k => renderRow(k, false))
                      )}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </div>
      </div>

      {/* ══ KATEGORİ EKLE/DÜZENLE MODAL ══════════════════════════ */}
      <Modal isOpen={modal} toggle={toggleModal} centered size="lg">
        <ModalHeader toggle={toggleModal} className="bg-light">
          {isEditing ? "Kategori Düzenle" : "Yeni Kategori Ekle"}
        </ModalHeader>
        <ModalBody>
          <Row>
            {/* Görsel */}
            <Col md={4}>
              <Label className="fw-semibold">Kategori Görseli</Label>
              <Dropzone onDrop={handleAcceptedFiles}>
                {({ getRootProps, getInputProps }) => (
                  <div
                    className="dropzone d-flex flex-column align-items-center justify-content-center border border-dashed rounded bg-light text-center"
                    style={{ minHeight: 180, cursor: "pointer" }}
                    {...getRootProps()}
                  >
                    <input {...getInputProps()} />
                    {imagePreview ? (
                      <>
                        <img src={imagePreview} alt="Önizleme" className="img-thumbnail mb-2" style={{ maxHeight: 130 }} />
                        <span className="text-primary fw-medium font-size-12">
                          <i className="bx bx-edit me-1" />Değiştir
                        </span>
                      </>
                    ) : (
                      <>
                        <i className="display-4 text-muted bx bxs-cloud-upload" />
                        <p className="mt-2 mb-0 font-size-13">Sürükle veya tıkla</p>
                      </>
                    )}
                  </div>
                )}
              </Dropzone>
            </Col>

            {/* Form alanları */}
            <Col md={8}>
              <Row>
                <Col md={8}>
                  <FormGroup className="mb-3">
                    <Label className="fw-semibold">
                      Kategori Adı <span className="text-danger">*</span>
                    </Label>
                    <Input
                      value={formData.AD}
                      onChange={e => setFormData({ ...formData, AD: e.target.value })}
                      placeholder="Örn: Tarım Makineleri"
                    />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup className="mb-3">
                    <Label className="fw-semibold">Sıra No</Label>
                    <Input
                      type="number"
                      value={formData.SIRA_NO}
                      onChange={e => setFormData({ ...formData, SIRA_NO: e.target.value })}
                      placeholder="1"
                    />
                  </FormGroup>
                </Col>
              </Row>

              {/* Üst Kategori Seçimi */}
              <FormGroup className="mb-3">
                <Label className="fw-semibold">
                  <i className="bx bx-git-branch me-1 text-warning" />
                  Üst Kategori (Alt Kategori Yapmak İçin)
                </Label>
                <Input
                  type="select"
                  value={secilenUstKategoriId}
                  onChange={e => setSecilenUstKategoriId(e.target.value)}
                >
                  <option value="">— Ana Kategori Olarak Ekle —</option>
                  {anaKategoriler
                    .filter(k => !isEditing || k.ID !== formData.ID)
                    .map(k => (
                      <option key={k.ID} value={k.ID}>{k.AD}</option>
                    ))
                  }
                </Input>
                <small className="text-muted">
                  Boş bırakırsanız ana kategori olarak eklenir.
                </small>
              </FormGroup>

              {/* Özellikler */}
              <div className="bg-light p-3 rounded border">
                <Label className="fw-semibold mb-3 border-bottom pb-2 d-block">
                  Modül İzinleri
                </Label>
                <Row>
                  <Col sm={6}>
                    <FormGroup switch className="mb-2">
                      <Input
                        type="switch"
                        checked={formData.MEZAT_ACIK_MI}
                        onChange={e => setFormData({ ...formData, MEZAT_ACIK_MI: e.target.checked })}
                      />
                      <Label check>Mezat Modülü Açık</Label>
                    </FormGroup>
                  </Col>
                  <Col sm={6}>
                    <FormGroup switch className="mb-2">
                      <Input
                        type="switch"
                        checked={formData.SADECE_ILAN_MI}
                        onChange={e => setFormData({ ...formData, SADECE_ILAN_MI: e.target.checked })}
                      />
                      <Label check>Sadece İlan</Label>
                    </FormGroup>
                  </Col>
                  <Col sm={6}>
                    <FormGroup switch className="mb-2">
                      <Input
                        type="switch"
                        checked={formData.IsPremium}
                        onChange={e => setFormData({ ...formData, IsPremium: e.target.checked })}
                      />
                      <Label check className="text-warning fw-bold">
                        <i className="bx bxs-star me-1" />Premium
                      </Label>
                    </FormGroup>
                  </Col>
                  <Col sm={6}>
                    <FormGroup switch className="mb-2">
                      <Input
                        type="switch"
                        checked={formData.Degerle_IsCheck}
                        onChange={e => setFormData({ ...formData, Degerle_IsCheck: e.target.checked })}
                      />
                      <Label check>Değerleme Açık</Label>
                    </FormGroup>
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={toggleModal}>İptal</Button>
          <Button color="primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? <Spinner size="sm" /> : <i className="bx bx-check-double me-1" />}
            {isEditing ? "Kaydet" : "Ekle"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* ══ ALT KATEGORİ BAĞLANTI YÖNETİMİ MODAL ════════════════ */}
      <Modal isOpen={baglantiModal} toggle={() => setBaglantiModal(false)} centered size="lg">
        <ModalHeader toggle={() => setBaglantiModal(false)} className="bg-light">
          <i className="bx bx-git-branch me-2 text-dark" />
          <strong>{baglantiKategori?.AD}</strong> — Alt Kategori Bağlantıları
        </ModalHeader>
        <ModalBody>
          {/* Mevcut bağlantılar */}
          <h6 className="fw-bold mb-3">Bağlı Alt Kategoriler ({mevcutAltKategoriler.length})</h6>
          {mevcutAltKategoriler.length === 0 ? (
            <div className="text-muted text-center py-3 bg-light rounded mb-3">
              Henüz alt kategori bağlı değil.
            </div>
          ) : (
            <div className="table-responsive mb-3">
              <Table className="table table-sm table-hover align-middle mb-0 border">
                <thead className="table-light">
                  <tr>
                    <th>Alt Kategori</th>
                    <th className="text-center">İlan</th>
                    <th className="text-center">Teklif</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {mevcutAltKategoriler.map(m => (
                    <tr key={m.ID}>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          {m.altKategori.KATEGORILER_IMAGE && (
                            <img
                              src={m.altKategori.KATEGORILER_IMAGE}
                              alt=""
                              style={{ width: 28, height: 28, objectFit: "cover", borderRadius: 4 }}
                            />
                          )}
                          <span className="fw-medium">{m.altKategori.AD}</span>
                          {m.altKategori.IsPremium && (
                            <Badge color="warning" className="font-size-10">Premium</Badge>
                          )}
                        </div>
                      </td>
                      <td className="text-center">
                        <span className="badge bg-primary">{ilanSayilari[m.altKategori.ID] || 0}</span>
                      </td>
                      <td className="text-center">
                        <span className="badge bg-success">{teklifSayilari[m.altKategori.ID] || 0}</span>
                      </td>
                      <td className="text-end">
                        <Button
                          color="danger" size="sm" outline
                          onClick={() => handleDeleteBaglanti(m.ID)}
                          title="Bağlantıyı Kaldır"
                        >
                          <i className="bx bx-unlink" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}

          {/* Yeni bağlantı ekle */}
          <div className="border-top pt-3">
            <h6 className="fw-bold mb-2">Alt Kategori Ekle</h6>
            <div className="d-flex gap-2">
              <Input
                type="select"
                value={yeniBaglantiId}
                onChange={e => setYeniBaglantiId(e.target.value)}
                className="flex-grow-1"
              >
                <option value="">— Kategori seçin —</option>
                {eklenebilirKategoriler.map(k => (
                  <option key={k.ID} value={k.ID}>
                    {k.AD} {altKategoriIdSet.has(k.ID) ? "(mevcut alt)" : "(ana)"}
                  </option>
                ))}
              </Input>
              <Button
                color="success"
                onClick={handleAddBaglanti}
                disabled={!yeniBaglantiId || isSavingBaglanti}
              >
                {isSavingBaglanti ? <Spinner size="sm" /> : <i className="bx bx-link-alt me-1" />}
                Bağla
              </Button>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setBaglantiModal(false)}>Kapat</Button>
        </ModalFooter>
      </Modal>

      {/* ══ KATEGORI İLANLARI MODAL ═══════════════════════════════ */}
      <Modal isOpen={ilanModal} toggle={() => setIlanModal(false)} size="xl" scrollable>
        <ModalHeader toggle={() => setIlanModal(false)} className="bg-light">
          <span className="text-primary fw-bold">{seciliKategori?.AD}</span>
          {" "} — Kategorideki İlanlar
        </ModalHeader>
        <ModalBody className="bg-light">
          {isLoadingIlanlar ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
              <p className="mt-2">Yükleniyor...</p>
            </div>
          ) : kategoriIlanlari.length > 0 ? (
            <div className="table-responsive">
              <Table className="table align-middle table-hover bg-white mb-0 shadow-sm rounded">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>İlan Adı</th>
                    <th>Satıcı</th>
                    <th>Fiyat</th>
                    <th>Tarih</th>
                    <th>Durum</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {kategoriIlanlari.map((ilan, idx) => (
                    <tr key={idx}>
                      <td><span className="fw-medium text-muted">#{ilan.ID}</span></td>
                      <td className="fw-medium">{ilan.AD || "-"}</td>
                      <td>{ilan.KULLANICILAR?.AD_SOYAD || "Bilinmiyor"}</td>
                      <td className="fw-bold text-primary">
                        {ilan.FIYAT ? `${Number(ilan.FIYAT).toLocaleString("tr-TR")} ₺` : "-"}
                      </td>
                      <td>
                        {ilan.OLUSTURMA_TARIHI
                          ? new Date(ilan.OLUSTURMA_TARIHI).toLocaleDateString("tr-TR")
                          : "-"}
                      </td>
                      <td>
                        <Badge color={ilan.AKTIF_DURUM === "true" || ilan.AKTIF_DURUM === true ? "success" : "danger"}>
                          {ilan.AKTIF_DURUM === "true" || ilan.AKTIF_DURUM === true ? "Yayında" : "Pasif"}
                        </Badge>
                      </td>
                      <td>
                        <Link
                          to={`/job-details/${ilan.ID}`}
                          className="btn btn-sm btn-light border"
                          onClick={() => setIlanModal(false)}
                        >
                          Git
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-5 bg-white rounded shadow-sm text-muted">
              <i className="bx bx-package display-4 mb-3 opacity-50" />
              <h5>Bu kategoride henüz ilan yok.</h5>
            </div>
          )}
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default KategoriYonetimi;
